import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import {
  Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  resolveLabel, useGetEffectivePoiTypeQuery,
  type InheritedFrom, type PoiField, type PoiFieldGroup, type PoiType, type ResolvedItem,
} from '@store/api/poiTypeEndpoints';
import { useState } from 'react';
import {
  buttonStyles, countPillStyles, dangerHoverStyles, dashedButtonStyles, fieldTableStyles, groupHeaderStyles,
  groupStyles, inheritBannerStyles, inheritedRegionHeaderStyles, inheritedStyles, requiredChipStyles,
} from '@pages/CustomPoiTypes.styles';
import {
  AngleDownIcon, AngleRightIcon, EditIcon, LayerGroupIcon, LockIcon, PlusIcon, SitemapIcon, TrashIcon,
} from 'react-line-awesome';
import { type GroupTarget } from './FieldDrawer';

/**
 * Everything one ancestor contributes, as one region of the page.
 *
 * `items` is ordered by ancestor, so a type's whole contribution arrives contiguous and a
 * region is just that stretch. Regioning is what lets the source be named once instead of
 * on every group header and above every field table.
 */
interface InheritedRegion {
  source: InheritedFrom;
  entries: ResolvedItem[];
}

/** The fields and groups of one type, as shown on its own page. */
interface Props {
  poiType: PoiType;
  /** Resolved name of the type this one extends, when it is one the reader can see. */
  parentName?: string;
  onOpenParent?: () => void;
  onAddField: (group: GroupTarget) => void;
  onEditField: (group: GroupTarget, index: number) => void;
  onDeleteField: (group: GroupTarget, index: number) => void;
  onAddGroup: () => void;
  onEditGroup: (index: number) => void;
  onDeleteGroup: (index: number) => void;
}

const PoiTypeSchema: React.FC<Props> = ({
  poiType, parentName, onOpenParent, onAddField, onEditField, onDeleteField, onAddGroup, onEditGroup, onDeleteGroup,
}) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  // Collapse is per group and lives here: it is view state, so it must not survive a
  // refetch as if it were part of the type.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const canEdit = has(poiType, 'poi_type:put');

  // Only a type that extends another needs the resolved view; a standalone type already
  // carries everything it has.
  const { currentData } = useGetEffectivePoiTypeQuery(poiType._id, {
    skip: !poiType.inheritFrom,
  });

  // `data` is not scoped to the hook argument — it holds the last result for *any* arg, so
  // walking from a type to its parent left the child's inherited fields rendered on the
  // parent's page. `currentData` is arg-scoped; the id check states the requirement
  // outright rather than relying on remembering which of the two is which.
  const effective = currentData?.id === poiType._id ? currentData : undefined;

  // Rendered in the API's own order rather than partitioned by kind. `items` states the
  // order a device draws in, and a page that regrouped it put every ancestor's fields in
  // one table at the top, detached from that ancestor's groups — so the page disagreed
  // with the contract it is showing.
  const inherited = effective?.items.filter((entry) => entry.inheritedFrom) ?? [];

  const inheritedRegions = inherited.reduce<InheritedRegion[]>((regions, entry) => {
    const current = regions[regions.length - 1];

    if (current && current.source?.id === entry.inheritedFrom?.id) {
      current.entries.push(entry);
    } else {
      regions.push({ source: entry.inheritedFrom, entries: [entry] });
    }

    return regions;
  }, []);

  const inheritedCount = inherited.reduce(
    (total, entry) => total + (entry.kind === 'field' ? 1 : entry.group.fields.length),
    0,
  );

  const ownFieldCount = poiType.fields.length
    + poiType.groups.reduce((total, group) => total + group.fields.length, 0);

  const label = (text: Parameters<typeof resolveLabel>[0]) => resolveLabel(text, locales);

  const toggleGroup = (key: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  /** One-line summary of a field's configuration, so the table says what the type does. */
  const summary = (field: PoiField): string => {
    switch (field.type) {
      case 'List':
      case 'MultiList':
        return (field.options ?? []).map((option) => label(option.label)).join(' · ') || '—';
      case 'Range':
        return `${field.min ?? '−'} – ${field.max ?? '−'}${field.decimal ? ' · dec' : ''}`
          + (field.value === undefined ? '' : ` · ${field.value}`);
      case 'Barcode':
        return (field.formats ?? []).join(', ') || t('Customization_AnyFormat');
      case 'Text':
        return field.multiline ? t('Customization_Multiline') : t('Customization_SingleLine');
      case 'Number':
        return field.decimal ? t('Customization_Decimal') : t('Customization_Integer');
      case 'Toggle':
        return field.value ? t('Customization_DefaultOn') : t('Customization_DefaultOff');
      default:
        return '—';
    }
  };

  const fieldRow = (field: PoiField, key: string, actions: React.ReactNode) => (
    <TableRow key={key} hover>
      <TableCell sx={{ width: '30%' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{label(field.label)}</Typography>
        {field.help && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {field.help}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ width: 150 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FieldTypeIcon type={field.type} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {t(`Customization_FieldType_${field.type}`)}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 108 }}>
        <Chip
          size="small"
          sx={requiredChipStyles}
          variant={field.required ? 'filled' : 'outlined'}
          color={field.required ? 'error' : 'default'}
          label={field.required ? t('Customization_Required') : t('Common_Optional')}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {summary(field)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ width: 92 }}>{actions}</TableCell>
    </TableRow>
  );

  /** Same shape as the detail header's actions: an icon, a tooltip, danger only on delete. */
  const iconAction = (
    title: string, allowed: boolean, icon: React.ReactNode, onClick: () => void, danger = false
  ) => (
    <Tooltip title={allowed ? title : t('Permission_NotAllowed')} arrow>
      <span>
        <IconButton
          size="small"
          disabled={!allowed}
          onClick={onClick}
          sx={danger ? dangerHoverStyles : { color: 'text.secondary' }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const ownActions = (group: GroupTarget, index: number) => (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      {iconAction(t('Common_Edit'), canEdit, <EditIcon />, () => onEditField(group, index))}
      {iconAction(t('Common_Delete'), canEdit, <TrashIcon />, () => onDeleteField(group, index), true)}
    </Stack>
  );

  const lockedAction = (source: string) => (
    <Tooltip title={t('Customization_InheritedFrom', [source])} arrow>
      <Box sx={{ color: 'text.disabled', display: 'inline-flex', fontSize: 15 }}>
        <LockIcon />
      </Box>
    </Tooltip>
  );

  const fieldTable = (rows: React.ReactNode) => (
    <Table size="small" sx={fieldTableStyles}>
      <TableBody>{rows}</TableBody>
    </Table>
  );

  const groupSection = (group: PoiFieldGroup, inheritedFromName: string | null, ownIndex: number | null) => {
    const key = `${poiType._id}:${group.id ?? ownIndex}`;
    const isCollapsed = collapsed.has(key);

    // No inherited tint here: an inherited group renders inside its ancestor's region,
    // which is already tinted, and applying it again stacked two alphas and made groups
    // read darker than the field table beside them.
    return (
      <Box key={key} sx={groupStyles}>
        <Stack direction="row" sx={groupHeaderStyles} onClick={() => toggleGroup(key)}>
          <Box
            sx={{
              color: 'text.disabled',
              display: 'inline-flex',
              fontSize: 16,
              transition: 'transform 150ms ease',
              transform: isCollapsed ? 'rotate(-90deg)' : 'none',
            }}
          >
            <AngleDownIcon />
          </Box>
          <Box sx={{ color: 'text.disabled', display: 'inline-flex', fontSize: 15 }}>
            <LayerGroupIcon />
          </Box>
          <Typography variant="subtitle2">{label(group.name)}</Typography>
          {group.description && (
            <Typography variant="caption" color="text.secondary">· {group.description}</Typography>
          )}
          <Box component="span" sx={countPillStyles}>{group.fields.length}</Box>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0.25} onClick={(event) => event.stopPropagation()}>
            {/* No source chip here: an inherited group sits inside a region already
                headed by the type it came from, and repeating it on every group was the
                noise this replaced. */}
            {inheritedFromName === null && ownIndex !== null && (
              <>
                {iconAction(t('Customization_AddField'), canEdit, <PlusIcon />, () => onAddField(ownIndex))}
                {iconAction(t('Customization_RenameGroup'), canEdit, <EditIcon />, () => onEditGroup(ownIndex))}
                {iconAction(t('Customization_DeleteGroup'), canEdit, <TrashIcon />, () => onDeleteGroup(ownIndex), true)}
              </>
            )}
          </Stack>
        </Stack>

        {!isCollapsed && (group.fields.length > 0
          ? fieldTable(group.fields.map((field, index) => fieldRow(
            field,
            `${key}-${field.id ?? index}`,
            inheritedFromName ? lockedAction(inheritedFromName) : ownActions(ownIndex ?? 'd', index),
          )))
          : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 6.5, pb: 1.5 }}>
              {t('Customization_NoFieldsInGroup')}
            </Typography>
          ))}
      </Box>
    );
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {poiType.inheritFrom && (
        <Stack direction="row" sx={inheritBannerStyles}>
          <Box sx={{ display: 'inline-flex', fontSize: 15 }}><SitemapIcon /></Box>
          {/* Naming the parent matters more than the count: it answers "where do I go to
              change these fields". Falls back to the count alone when the parent is not
              among the types this reader can see. */}
          <Typography variant="caption" sx={{ flexGrow: 1, minWidth: 0 }}>
            {parentName
              ? t('Customization_ExtendsBanner', [parentName, inheritedCount])
              : t('Customization_InheritsBanner', [inheritedCount])}
          </Typography>
          {parentName && onOpenParent && (
            <Button size="small" color="inherit" endIcon={<AngleRightIcon />} onClick={onOpenParent} sx={buttonStyles}>
              {t('Customization_OpenParent')}
            </Button>
          )}
        </Stack>
      )}

      {/* Inherited content first and locked, one region per ancestor, oldest first — the
          order `items` resolves in. Within a region the API emits that type's ungrouped
          fields before its groups, so rendering the table above the sections reproduces
          the resolved order rather than reordering it. */}
      {inheritedRegions.map((region) => {
        const sourceName = label(region.source?.name);
        const regionFields = region.entries.filter((entry) => entry.kind === 'field');
        const regionGroups = region.entries.filter((entry) => entry.kind === 'group');

        return (
          <Box key={`region-${region.source?.id}`} sx={{ ...groupStyles, ...inheritedStyles }}>
            <Stack direction="row" sx={inheritedRegionHeaderStyles}>
              <Box sx={{ color: 'primary.main', display: 'inline-flex', fontSize: 14 }}>
                <SitemapIcon />
              </Box>
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }} noWrap>
                {sourceName}
              </Typography>
            </Stack>

            {regionFields.length > 0 && fieldTable(regionFields.map((entry, index) => fieldRow(
              entry.field,
              `inh-${entry.field.id ?? index}`,
              lockedAction(sourceName),
            )))}

            {regionGroups.map((entry) => groupSection(entry.group, sourceName, null))}
          </Box>
        );
      })}

      {poiType.fields.length > 0 && (
        <Box sx={groupStyles}>
          {fieldTable(poiType.fields.map((field, index) => fieldRow(
            field, `own-${field.id ?? index}`, ownActions('d', index),
          )))}
        </Box>
      )}
      {poiType.groups.map((group, index) => groupSection(group, null, index))}

      {ownFieldCount === 0 && poiType.groups.length === 0 && inheritedCount === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.25, py: 3, textAlign: 'center' }}>
          {t('Customization_NoFieldsYet')}
        </Typography>
      )}

      <Stack direction="row" spacing={1.25} sx={{ px: 2.25, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button size="small" startIcon={<PlusIcon />} disabled={!canEdit} sx={dashedButtonStyles} onClick={() => onAddField('d')}>
          {t('Customization_AddField')}
        </Button>
        <Button size="small" startIcon={<PlusIcon />} disabled={!canEdit} sx={dashedButtonStyles} onClick={onAddGroup}>
          {t('Customization_AddGroup')}
        </Button>
      </Stack>
    </Paper>
  );
};

export default PoiTypeSchema;
